/**
 * Bet Ticker "More" filter popover — Lab-style overflow for secondary filters.
 */
import { Button, Field, Popover } from '@w7/ui'
import { useRef, useState } from 'react'
import {
  TICKER_CURRENCIES,
  type TickerFilters,
} from './domain'
import { countMoreFilters, orderStakeBounds } from './filters'
import { TICKER_MARKET_TYPES } from './marketType'

function stakeFromInput(raw: string): number | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  const n = Number(trimmed)
  if (!Number.isFinite(n) || n < 0) return null
  return Math.round(n * 100) / 100
}

export type TickerMoreFiltersProps = {
  filters: TickerFilters
  setFilters: (patch: Partial<TickerFilters>) => void
  sports: string[]
  events: string[]
  markets: string[]
  /** Live/Previous — Flags lives under More; Flag Final keeps it on the toolbar. */
  showFlagsFilter?: boolean
}

export function TickerMoreFilters({
  filters,
  setFilters,
  sports,
  events,
  markets,
  showFlagsFilter = false,
}: TickerMoreFiltersProps) {
  const anchor = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const active = countMoreFilters(filters, { includeFlagged: showFlagsFilter })

  return (
    <>
      <Button
        ref={anchor}
        type="button"
        variant="secondary"
        icon="filter"
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid="ticker-more-filters-btn"
        onClick={() => setOpen((v) => !v)}
      >
        {active > 0 ? `More (${active})` : 'More'}
      </Button>

      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchor}
        label="More filters"
        align="end"
        width={340}
      >
        <div className="w7-bm-more-filters" data-testid="ticker-more-filters">
          <Field label="Sub game">
            {({ id }) => (
              <select
                id={id}
                className="w7-bm-select"
                aria-label="Sub Game"
                value={filters.sport}
                onChange={(e) =>
                  setFilters({ sport: e.target.value, event: '', market: '' })
                }
              >
                <option value="all">All sub games</option>
                {sports.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="Event">
            {({ id }) => (
              <select
                id={id}
                className="w7-bm-select"
                aria-label="Event"
                value={filters.event}
                onChange={(e) => setFilters({ event: e.target.value, market: '' })}
              >
                <option value="">All events</option>
                {filters.event && !events.includes(filters.event) ? (
                  <option value={filters.event}>{filters.event}</option>
                ) : null}
                {events.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="Market type">
            {({ id }) => (
              <select
                id={id}
                className="w7-bm-select"
                aria-label="Market type"
                value={filters.marketType}
                onChange={(e) =>
                  setFilters({ marketType: e.target.value, market: '' })
                }
              >
                <option value="all">All market types</option>
                {TICKER_MARKET_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="Market">
            {({ id }) => (
              <select
                id={id}
                className="w7-bm-select"
                aria-label="Market"
                value={filters.market}
                onChange={(e) => setFilters({ market: e.target.value })}
              >
                <option value="">All markets</option>
                {filters.market && !markets.includes(filters.market) ? (
                  <option value={filters.market}>{filters.market}</option>
                ) : null}
                {markets.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="Currency">
            {({ id }) => (
              <select
                id={id}
                className="w7-bm-select"
                aria-label="Currency"
                value={filters.currency}
                onChange={(e) => setFilters({ currency: e.target.value })}
              >
                <option value="all">All currencies</option>
                {TICKER_CURRENCIES.map((ccy) => (
                  <option key={ccy} value={ccy}>
                    {ccy}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <div className="w7-bm-more-pair" role="group" aria-label="Stake range">
            <Field label="Min stake">
              {({ id }) => (
                <input
                  id={id}
                  className="w7-bm-input"
                  type="number"
                  min={0}
                  step="any"
                  inputMode="decimal"
                  placeholder="0"
                  aria-label="Stake from"
                  value={filters.stakeMin ?? ''}
                  onChange={(e) => setFilters({ stakeMin: stakeFromInput(e.target.value) })}
                  onBlur={() => {
                    const next = orderStakeBounds(filters.stakeMin, filters.stakeMax)
                    if (
                      next.stakeMin !== filters.stakeMin ||
                      next.stakeMax !== filters.stakeMax
                    ) {
                      setFilters(next)
                    }
                  }}
                />
              )}
            </Field>
            <Field label="Max stake">
              {({ id }) => (
                <input
                  id={id}
                  className="w7-bm-input"
                  type="number"
                  min={0}
                  step="any"
                  inputMode="decimal"
                  placeholder="0"
                  aria-label="Stake to"
                  value={filters.stakeMax ?? ''}
                  onChange={(e) => setFilters({ stakeMax: stakeFromInput(e.target.value) })}
                  onBlur={() => {
                    const next = orderStakeBounds(filters.stakeMin, filters.stakeMax)
                    if (
                      next.stakeMin !== filters.stakeMin ||
                      next.stakeMax !== filters.stakeMax
                    ) {
                      setFilters(next)
                    }
                  }}
                />
              )}
            </Field>
          </div>

          {showFlagsFilter ? (
            <Field label="Flags">
              {({ id }) => (
                <select
                  id={id}
                  className="w7-bm-select"
                  aria-label="Flag"
                  value={filters.flagged}
                  onChange={(e) => {
                    const v = e.target.value
                    setFilters({
                      flagged: v === 'flagged' || v === 'final' ? v : 'all',
                    })
                  }}
                >
                  <option value="all">All flags</option>
                  <option value="flagged">Flagged bets</option>
                </select>
              )}
            </Field>
          ) : null}
        </div>
      </Popover>
    </>
  )
}
